import { FaroTooltip } from "@faro-lotv/flat-ui";
import { Box, ButtonBase } from "@mui/material";
import { Stack } from "@mui/system";
import { DateTime } from "luxon";
import { memo, useCallback, useMemo, type JSX, type MouseEvent } from "react";
import { MONTH_AND_YEAR, formatDate } from "../capture-date-format";
import { groupBucketsByMonth } from "../capture-date-stats";
import { CalendarHeader } from "./calendar-header";
import { bandSx, cellSx, rangeRoleOf } from "./calendar-styles";
import {
  captureLabel,
  useBucketCounts,
  usePageTurnOnRest,
} from "./calendar-utils";
import {
  CalendarViewProps,
  CaptureGranularity,
} from "./capture-calendar-types";
import { CaptureCountChip } from "./capture-markers";

const MONTHS_PER_YEAR = 12;
const MONTHS_PER_ROW = 3;

/** @returns A year of oval month cells, each showing how many captures it holds */
export function MonthGrid({
  stats,
  startDate,
  endDate,
  onPick,
  onHover,
  onPress,
  isDragging,
  isPickable,
  month,
  onMonthChange,
  bounds,
  onToggleView,
}: CalendarViewProps): JSX.Element {
  const months = useMemo(() => groupBucketsByMonth(stats.days), [stats.days]);
  const counts = useBucketCounts(months);
  const year = useMemo(() => month.startOf("year"), [month]);
  const monthCells = useMemo(
    () =>
      Array.from({ length: MONTHS_PER_YEAR }, (_, index) =>
        year.plus({ months: index }),
      ),
    [year],
  );
  const thisMonth = useMemo(() => DateTime.now().startOf("month"), []);
  const pageTurn = usePageTurnOnRest(isDragging);
  const isPreviousDisabled = year <= bounds.min.startOf("year");
  const isNextDisabled = year >= bounds.max.startOf("year");
  const previousYear = useCallback((): void => {
    onMonthChange(month.minus({ years: 1 }));
  }, [month, onMonthChange]);
  const nextYear = useCallback((): void => {
    onMonthChange(month.plus({ years: 1 }));
  }, [month, onMonthChange]);

  return (
    <Stack sx={{ userSelect: "none" }}>
      <CalendarHeader
        label={formatDate(year, { year: "numeric" })}
        granularity={CaptureGranularity.month}
        onToggleView={onToggleView}
        onPrevious={previousYear}
        onNext={nextYear}
        isPreviousDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        isDragging={isDragging}
      />

      <Box
        component="div"
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${MONTHS_PER_ROW}, 1fr)`,
          rowGap: 1,
        }}
      >
        {monthCells.map((cell, index) => {
          const count = counts.get(cell.toMillis()) ?? 0;
          const column = index % MONTHS_PER_ROW;
          const isDisabled =
            cell < bounds.min.startOf("month") ||
            cell > bounds.max ||
            !isPickable(cell, "month");

          return (
            <FaroTooltip
              key={cell.toMillis()}
              placement="top"
              title={`${formatDate(cell, MONTH_AND_YEAR)} — ${captureLabel(count)}`}
              sx={[
                bandSx(
                  rangeRoleOf(cell, "month", startDate, endDate),
                  column === 0,
                  column === MONTHS_PER_ROW - 1,
                ),
                { px: 0.75 },
              ]}
            >
              <MonthGridButton
                index={index}
                cell={cell}
                count={count}
                isDisabled={isDisabled}
                thisMonth={thisMonth}
                startDate={startDate}
                endDate={endDate}
                isPreviousDisabled={isPreviousDisabled}
                isNextDisabled={isNextDisabled}
                pageTurn={pageTurn}
                onPick={onPick}
                onHover={onHover}
                onPress={onPress}
                previousYear={previousYear}
                nextYear={nextYear}
              />
            </FaroTooltip>
          );
        })}
      </Box>
    </Stack>
  );
}

type MonthGridButtonProps = {
  index: number;
  cell: DateTime;
  count: number;
  isDisabled: boolean;
  thisMonth: DateTime;
  startDate?: Date;
  endDate?: Date;
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  pageTurn: ReturnType<typeof usePageTurnOnRest>;
  onPick: CalendarViewProps["onPick"];
  onHover: CalendarViewProps["onHover"];
  onPress: CalendarViewProps["onPress"];
  previousYear(): void;
  nextYear(): void;
};

const MonthGridButton = memo(function MonthGridButton({
  index,
  cell,
  count,
  isDisabled,
  thisMonth,
  startDate,
  endDate,
  isPreviousDisabled,
  isNextDisabled,
  pageTurn,
  onPick,
  onHover,
  onPress,
  previousYear,
  nextYear,
}: MonthGridButtonProps): JSX.Element {
  const role = rangeRoleOf(cell, "month", startDate, endDate);
  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      onPick(cell, "month", event.shiftKey);
    },
    [cell, onPick],
  );
  const onPointerEnter = useCallback((): void => {
    onHover?.([cell, "month"]);
    if (index === 0 && !isPreviousDisabled) {
      pageTurn.rest(previousYear);
    } else if (index === MONTHS_PER_YEAR - 1 && !isNextDisabled) {
      pageTurn.rest(nextYear);
    }
  }, [
    cell,
    index,
    isNextDisabled,
    isPreviousDisabled,
    nextYear,
    onHover,
    pageTurn,
    previousYear,
  ]);
  const onPointerLeave = useCallback((): void => {
    onHover?.();
    pageTurn.leave();
  }, [onHover, pageTurn]);
  const onPointerDown = useCallback((): void => {
    onPress?.([cell, "month"]);
  }, [cell, onPress]);

  return (
    <ButtonBase
      aria-label={`${formatDate(cell, MONTH_AND_YEAR)} ${captureLabel(count)}`}
      aria-pressed={role.isSelected}
      disabled={isDisabled}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      sx={[
        cellSx(role, cell.hasSame(thisMonth, "month"), isDisabled),
        {
          width: "100%",
          height: "34px",
          gap: 0.75,
          fontSize: "13px",
        },
      ]}
    >
      {formatDate(cell, { month: "short" })}
      {count > 0 && <CaptureCountChip count={count} />}
    </ButtonBase>
  );
});
